import {useDefaultQuery} from "../../../../common/apis/ReactQuery";
import userGroupApiClient from "../../../../common/apis/usergroup/UserGroupApiClient";
import {QueryClient, useMutation, useQueryClient} from "@tanstack/react-query";
import {invalidateFindUserGroupsByLoginQueryCache} from "../users/UserQuery";

export function useFindAllUserGroupsQuery() {
    return useDefaultQuery({
        queryKey: ["userGroups"],
        queryFn: () => userGroupApiClient.findAll(),
    });
}

export function invalidateFindAllUserGroupsQueryCache(queryClient: QueryClient) {
    queryClient.invalidateQueries({
        queryKey: ["userGroups"],
        exact: true,
    });
}


export function useFindUserGroupByIdQuery(groupId: string) {
    return useDefaultQuery({
        queryKey: ["userGroups", groupId],
        queryFn: () => userGroupApiClient.findById(groupId),
    });
}

export function invalidateFindUserGroupByIdQueryCache(groupId: string, queryClient: QueryClient) {
    queryClient.invalidateQueries({
        queryKey: ["userGroups", groupId],
        exact: true,
    });
}

export function useFindAllUserGroupMembersQuery(groupId: string) {
    return useDefaultQuery({
        queryKey: ["userGroups", groupId, "members"],
        queryFn: () => userGroupApiClient.findAllMembers(groupId),
    });
}

export function invalidateFindAllUserGroupMembersQueryCache(groupId: string, queryClient: QueryClient) {
    queryClient.invalidateQueries({
        queryKey: ["userGroups", groupId, "members"],
        exact: true,
    });
}

export function useRemoveUserGroupMemberMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({groupId, userLogin}: { groupId: string; userLogin: string }) =>
                userGroupApiClient.removeMember(groupId, userLogin),
        onSuccess: (_data, {groupId, userLogin}) => {
            invalidateFindAllUserGroupMembersQueryCache(groupId, queryClient);
            invalidateFindUserGroupsByLoginQueryCache(userLogin, queryClient);
        },
    });
}

export function useAddUserGroupMembershipsMutation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({userLogin, groupIds}: { userLogin: string; groupIds: string[] }) =>
                Promise.all(groupIds.map(groupId => userGroupApiClient.addMember(groupId, userLogin))),
        onSuccess: (_data, {userLogin, groupIds}) => {
            invalidateFindUserGroupsByLoginQueryCache(userLogin, queryClient);
            groupIds.forEach(groupId => invalidateFindAllUserGroupMembersQueryCache(groupId, queryClient));
        },
    });
}

export function useFindAllUserGroupPermissionsQuery(groupId: string) {
    return useDefaultQuery({
        queryKey: ["userGroups", groupId, "permissions"],
        queryFn: () => userGroupApiClient.findAllPermissions(groupId),
    });
}

export function invalidateFindAllUserGroupPermissionsQueryCache(groupId: string, queryClient: QueryClient) {
    queryClient.invalidateQueries({
        queryKey: ["userGroups", groupId, "permissions"],
        exact: true,
    });
}
